/**
 * "Practice safely with a test deal" guided tour (issue #2195, REQ D4-ONB-016).
 *
 * Converts Matt's "Creating & Removing a Test Deal" how-to into an in-app GUIDED
 * tour ("show me, not tell me") so a first-time user can practise safely with
 * guidance on the page. It is LAUNCHER-ONLY: relaunchable from the Training
 * Guided Tours panel and deep-linkable via ``?tour=test-deal``. It deliberately
 * does NOT claim the ``/crm/deals`` route launcher / first-visit nudge slot --
 * that stays the ``agent-deals`` section tour (routeTours.ts / sectionNudges.ts
 * are unchanged for ``/crm/deals``; this tour is added to the nudge exclusion
 * set so it is offered from Training only).
 *
 * AUDIENCE: offered to the personas who actually have a deal book to practise
 * in -- a field agent, an admin, and the deal-desk presets (paralegal, finance,
 * finance/accounts, manager) whose deal list reads the live company book. The
 * presets that do not capture or tidy deals -- canvasser, HR, the office
 * administrator, and accounts (the deposits desk: it reads the company deal
 * list, defaulting to the Outstanding view, but never captures a deal) -- are
 * excluded (``excludePreset``), per issue #3610's scope. A null preset (an admin, or
 * a plain agent) is never excluded. The single capability that not everyone
 * holds -- having a test deal erased for good -- is fenced on its own step with
 * ``adminOnly``, the SAME ``user.role === "admin"`` condition DealList.tsx
 * gates its admin-only list controls on (D4-ONB-004). The list itself offers
 * archive/restore only; the step says so rather than naming a button.
 *
 * Conventions (mirror agentSectionTours.ts, the section-tour template):
 *  - Copy rules (D4-ONB-011 / D4-WLB): agency-neutral via the ``{agency}`` token
 *    (never a hardcoded agency/vendor name -- the D4-WLB-008 grep guard stays
 *    green); plain professional language for non-technical realtors; NO emojis.
 *  - page=spotlight / action=beacon (#1341/#1342): the orientation / archive
 *    steps spotlight their region (default ``page`` kind), the Add Lease / Add
 *    Sale steps beacon their button (``kind: "action"``).
 *  - Every step lives on ``/crm/deals`` and anchors a STABLE ``data-tour="..."``
 *    element that is ALWAYS present (the header, the Archived toggle, the Add
 *    Sale / Add Lease buttons) -- never the conditional bulk action bar, so no
 *    step can fire TARGET_NOT_FOUND and end the tour early.
 */
import type { TourDefinition } from "./types";

/**
 * Presets with no deal book to practise in -- withheld from the test-deal tour.
 * Every other preset (and a null preset: an admin or a plain agent) keeps it.
 */
export const TEST_DEAL_EXCLUDED_PRESETS = [
  "canvasser",
  "hr",
  "accounts",
  "office_admin",
];

export const TEST_DEAL_TOUR: TourDefinition = {
  id: "test-deal",
  title: "Test deal tour",
  audience: { excludePreset: TEST_DEAL_EXCLUDED_PRESETS },
  steps: [
    {
      // Orientation / reassurance -- the heart of the guide. Ungated centered
      // narrative so the tour always starts for any persona.
      id: "safe-to-test",
      route: "/crm/deals",
      target: "center",
      section: "Practice safely",
      title: "Practice safely with a test deal",
      body: "Want to try things out before you go live on {agency}? Create a test deal. It is completely safe: a test deal is just a normal deal you tidy up afterwards -- creating one sends NO emails or WhatsApp messages and creates NO invoices or commission payments. There is no separate sandbox, so label it obviously, for example an address of ZZ TEST -- DELETE ME and a round price, then remove it when you are done. You can leave this tour any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "pipeline",
      route: "/crm/deals",
      target: "page-deals",
      section: "Where deals live",
      title: "Where your deals appear",
      body: "This is your deals list. A test deal you create simply appears here alongside your real ones -- which is exactly why a clear label like ZZ TEST keeps it easy to spot and clean up later.",
      placement: "bottom",
    },
    {
      // ACTION step: beacon on Add Lease -- the frictionless practice path.
      id: "add-lease",
      route: "/crm/deals",
      target: "deals-add-lease",
      kind: "action",
      section: "Add a lease",
      title: "Easiest practice path: Add Lease",
      body: "For casual practice, Add Lease is the simplest route -- a lease does not need a LOOM or Deeds-Office report, so nothing blocks you. Give the beacon a click to see where it sits. Fill in obvious test details and save when you want to try it for real.",
      placement: "left",
    },
    {
      // ACTION step: beacon on Add Sale -- note the Loom report + sign-off.
      id: "add-sale",
      route: "/crm/deals",
      target: "deals-add-sale",
      kind: "action",
      section: "Add a sale",
      title: "Add Sale needs a property report",
      body: "Add Sale captures a sale. Note that a sale needs a LOOM or Deeds-Office property report first and then goes to the manager sign-off queue, so it is a little more involved than a lease. An admin can create a sale instantly, skipping sign-off and the report. Give the beacon a click to find the button.",
      placement: "left",
    },
    {
      // PAGE step on the always-present Archived toggle. Explains the tick ->
      // Archive -> Restore flow WITHOUT relying on the conditional bulk action
      // bar being open at step time (it only renders once a row is selected).
      id: "archive-restore",
      route: "/crm/deals",
      target: "deals-archived-toggle",
      section: "Tidy up",
      title: "Tidy up: Archive and Restore",
      body: "Done practising? To tidy a test deal away, tick the box next to it in the list -- a bar appears with an Archive button, and archiving moves the deal out of your active list. It is fully reversible: use this Archived toggle to switch to the Archived view, tick the deal and choose Restore to bring it straight back. Nothing is lost.",
      placement: "bottom",
    },
    {
      // ADMIN-ONLY awareness step (D4-ONB-004). Gated on the admin ROLE --
      // the same ``user.role === "admin"`` condition DealList.tsx gates its
      // admin-only list controls on. The deal list offers NO permanent-delete
      // control (archive/restore only), so the copy says so honestly rather
      // than pointing at a button that does not exist. Anchored on the
      // always-present header region.
      id: "permanent-delete",
      route: "/crm/deals",
      target: "page-deals",
      adminOnly: true,
      section: "Remove for good",
      title: "Removing a test deal for good",
      body: "Archiving is how a test deal leaves the list, and it is always reversible -- this page has no permanent-delete button, for anyone. If a test deal really must be erased with no trace, ask your system support contact to remove it: a permanent delete cannot be undone, which is why archive is the safe default. That is the test-deal walkthrough -- practise away.",
      placement: "bottom",
    },
  ],
};
